import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

function LogoFallback({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#00182A"/>
      <text x="24" y="32" textAnchor="middle" fill="white" fontSize="18" fontWeight="800" fontFamily="Montserrat,sans-serif">RH</text>
      <circle cx="38" cy="10" r="6" fill="#00C389"/>
      <path d="M35 10 L38 7 L41 10 L38 13 Z" fill="white"/>
    </svg>
  );
}

function Logo({ size = 48 }: { size?: number }) {
  const [error, setError] = React.useState(false);
  if (error) return <LogoFallback size={size} />;
  return (
    <img
      src="/logo.png"
      alt="Logo"
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className="object-contain rounded-lg flex-shrink-0"
      onError={() => setError(true)}
    />
  );
}

export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await signIn(email, password);
    if (error) {
      setError('Credenciales incorrectas. Verifica tu email y contraseña.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center relative overflow-hidden px-6">
      {/* Background blobs */}
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-secondary-container/20 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md z-10 animate-fade-in">
        {/* Brand */}
        <div className="mb-10 text-center flex flex-col items-center gap-3">
          <Logo size={64} />
          <div>
            <h1 className="font-headline font-extrabold text-3xl tracking-tight text-on-surface mb-0.5">
              RRHH Up Solutions
            </h1>
            <p className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">
              Sistema de Planillas Perú
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-surface-container-lowest rounded-xl p-10 shadow-[0_20px_40px_rgba(25,28,30,0.08)] border border-outline-variant/10">
          <div className="mb-8">
            <h2 className="font-headline font-bold text-2xl text-on-surface mb-1">Bienvenido</h2>
            <p className="text-on-surface-variant text-sm">Ingresa tus credenciales corporativas</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                Email corporativo
              </label>
              <div className="flex items-center bg-surface-container-highest/30 rounded-t-lg border-b border-outline-variant/30 focus-within:border-primary transition-colors">
                <span className="material-symbols-outlined ml-4 text-outline text-[20px]">corporate_fare</span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="usuario@empresa.com"
                  required
                  className="w-full bg-transparent border-none focus:ring-0 py-4 px-4 text-sm outline-none"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                Contraseña
              </label>
              <div className="flex items-center bg-surface-container-highest/30 rounded-t-lg border-b border-outline-variant/30 focus-within:border-primary transition-colors">
                <span className="material-symbols-outlined ml-4 text-outline text-[20px]">lock</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-transparent border-none focus:ring-0 py-4 px-4 text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="px-4 text-outline hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-[20px]">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error-container rounded-lg">
                <span className="material-symbols-outlined text-error text-[18px]">error</span>
                <p className="text-xs text-on-error-container">{error}</p>
              </div>
            )}

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary text-white font-bold text-sm uppercase tracking-wider py-4 rounded-lg shadow-lg shadow-primary/20 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Iniciando sesión...
                  </span>
                ) : 'Iniciar Sesión'}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-[10px] font-semibold text-on-surface-variant uppercase tracking-widest">
              Sistemas operativos
            </span>
          </div>
          <p className="text-[10px] text-outline uppercase tracking-widest">
            © 2025 RRHH Up Solutions
          </p>
        </div>
      </div>
    </div>
  );
}
